import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { CallRecordingPlayer } from '../components/CallRecordingPlayer';
import { yeastarAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import {
  PhoneCall, PhoneMissed, Voicemail, PhoneOutgoing, PhoneIncoming,
  Clock, Search, Loader2, ExternalLink, User, Download, Activity,
  CheckCircle2, XCircle, RefreshCw, ChevronDown,
} from 'lucide-react';

const fmtDuration = (s) => {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m ? `${m}m ${sec}s` : `${sec}s`;
};

const KIND_META = {
  received: { label: 'Received', icon: PhoneIncoming, cls: 'bg-green-100 text-green-700 border-green-200' },
  missed: { label: 'Missed', icon: PhoneMissed, cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  voicemail: { label: 'Voicemail', icon: Voicemail, cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  outbound: { label: 'Outbound', icon: PhoneOutgoing, cls: 'bg-sky-100 text-sky-700 border-sky-200' },
};

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'received', label: 'Received' },
  { key: 'missed', label: 'Missed' },
  { key: 'voicemail', label: 'Voicemail' },
  { key: 'outbound', label: 'Outbound' },
];

export const CallAnalytics = () => {
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(30);
  const [diag, setDiag] = useState([]);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);

  const loadDiag = useCallback(() => {
    setDiagLoading(true);
    yeastarAPI.inboundLog()
      .then((r) => setDiag(r.data || []))
      .catch(() => setDiag(null))
      .finally(() => setDiagLoading(false));
  }, []);

  useEffect(() => { loadDiag(); }, [loadDiag]);

  const exportCsv = () => {
    const headers = ['When', 'Type', 'Direction', 'Customer', 'Number', 'From', 'To', 'Handled By', 'Duration (s)', 'Recording', 'Status'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = logs.map((l) => [
      formatDateTime(l.started_at), (KIND_META[l.kind] || {}).label || l.kind, l.direction || '',
      l.customer_name || '', l.peer_number || '', l.from_number || '', l.to_number || '',
      l.staff_name || '', l.talk_duration || 0, l.has_recording ? 'Yes' : 'No', l.status || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    yeastarAPI.analytics(days).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [days]);

  const loadLogs = useCallback(() => {
    setLoading(true);
    yeastarAPI.callLogs({ kind, search, days, limit: 500 })
      .then((r) => setLogs(r.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [kind, search, days]);

  useEffect(() => {
    const t = setTimeout(loadLogs, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [loadLogs, search]);

  const c = summary?.counts || {};
  const StatCard = ({ icon, label, value, color, testid }) => (
    <Card className="border-slate-200">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>{icon}</div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-slate-900" data-testid={testid}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="call-analytics-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">Call Analytics</h1>
            <p className="text-sm text-slate-500 mt-1">Every call logged from the phone system, with volumes, missed rate and recordings.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={logs.length === 0} data-testid="call-export-csv">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <select
              className="border rounded-md h-9 px-3 text-sm bg-white"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="call-days-select"
            >
              <option value={1}>Today</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="call-summary">
          <StatCard icon={<PhoneCall className="w-5 h-5 text-slate-600" />} label="Total" value={summary?.total || 0} color="bg-slate-100" testid="stat-total" />
          <StatCard icon={<PhoneIncoming className="w-5 h-5 text-green-600" />} label="Received" value={c.received || 0} color="bg-green-100" testid="stat-received" />
          <StatCard icon={<PhoneMissed className="w-5 h-5 text-rose-600" />} label="Missed" value={c.missed || 0} color="bg-rose-100" testid="stat-missed" />
          <StatCard icon={<Voicemail className="w-5 h-5 text-amber-600" />} label="Voicemail" value={c.voicemail || 0} color="bg-amber-100" testid="stat-voicemail" />
          <StatCard icon={<PhoneOutgoing className="w-5 h-5 text-sky-600" />} label="Outbound" value={c.outbound || 0} color="bg-sky-100" testid="stat-outbound" />
          <StatCard icon={<Clock className="w-5 h-5 text-indigo-600" />} label="Avg talk" value={fmtDuration(summary?.avg_talk_seconds)} color="bg-indigo-100" testid="stat-avg-talk" />
        </div>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-green-600" /> Call Logs
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex bg-slate-100 rounded-lg p-0.5">
                  {FILTERS.map((f) => (
                    <button
                      key={f.key || 'all'}
                      onClick={() => setKind(f.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${kind === f.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      data-testid={`call-filter-${f.key || 'all'}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input
                    className="pl-8 h-9 w-52 text-sm"
                    placeholder="Search number or name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="call-search"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : logs.length === 0 ? (
              <div className="text-center py-16 text-slate-400" data-testid="call-logs-empty">
                <PhoneCall className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No calls logged for this period.</p>
                <p className="text-xs mt-1">Calls appear here automatically once your phone system starts journaling them to the CRM.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="call-logs-table">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100">
                      <th className="py-2 pr-4 font-medium">When</th>
                      <th className="py-2 pr-4 font-medium">Type</th>
                      <th className="py-2 pr-4 font-medium">Customer</th>
                      <th className="py-2 pr-4 font-medium">Number</th>
                      <th className="py-2 pr-4 font-medium">Handled by</th>
                      <th className="py-2 pr-4 font-medium">Duration</th>
                      <th className="py-2 pr-4 font-medium">Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l) => {
                      const meta = KIND_META[l.kind] || KIND_META.received;
                      const Icon = meta.icon;
                      return (
                        <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`call-log-row-${l.id}`}>
                          <td className="py-2.5 pr-4 whitespace-nowrap text-slate-600">{formatDateTime(l.started_at)}</td>
                          <td className="py-2.5 pr-4">
                            <Badge className={`${meta.cls} gap-1 font-medium`}>
                              <Icon className="w-3 h-3" /> {meta.label}
                            </Badge>
                          </td>
                          <td className="py-2.5 pr-4">
                            {l.customer_id ? (
                              <Link to={`/customers/${l.customer_id}?tab=history`} className="inline-flex items-center gap-1 text-slate-800 font-medium hover:text-blue-600 group">
                                {l.customer_name || 'Customer'}
                                <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-blue-500" />
                              </Link>
                            ) : (
                              <span className="text-slate-400">Unknown</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-4 whitespace-nowrap text-slate-600">{l.peer_number || l.from_number || l.to_number || '—'}</td>
                          <td className="py-2.5 pr-4 whitespace-nowrap text-slate-600">
                            {l.staff_name ? (
                              <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5 text-slate-400" />{l.staff_name}</span>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="py-2.5 pr-4 whitespace-nowrap text-slate-600">{fmtDuration(l.talk_duration)}</td>
                          <td className="py-2.5 pr-4">
                            {l.has_recording ? <CallRecordingPlayer callLogId={l.id} /> : <span className="text-xs text-slate-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PBX connection diagnostics — helps confirm the phone system is reaching the CRM */}
        {diag !== null && (
          <Card className="border-slate-200" data-testid="pbx-diagnostics">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setDiagOpen((o) => !o)}
                  className="flex items-center gap-2 text-left"
                  data-testid="pbx-diagnostics-toggle"
                >
                  <Activity className="w-5 h-5 text-indigo-500" />
                  <div>
                    <CardTitle className="text-lg font-semibold font-['Manrope']">PBX connection activity</CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {diag.length === 0
                        ? 'No requests from your phone system have reached the CRM yet.'
                        : `${diag.length} recent request(s) from your phone system`}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${diagOpen ? 'rotate-180' : ''}`} />
                </button>
                <Button variant="ghost" size="sm" onClick={loadDiag} disabled={diagLoading} data-testid="pbx-diagnostics-refresh">
                  {diagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
            </CardHeader>
            {diagOpen && (
              <CardContent>
                {diag.length === 0 ? (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                    <p className="font-medium mb-1">No inbound requests recorded.</p>
                    <p className="text-amber-700">Your Yeastar PBX hasn't successfully reached the CRM. Check on the Yeastar portal that the Custom CRM template is imported <span className="font-medium">and enabled</span>, and that its URL points at this CRM. If you're on production, confirm the template's API key matches the one in Settings → Yeastar Phone.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pbx-diagnostics-table">
                      <thead>
                        <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100">
                          <th className="py-2 pr-4 font-medium">When</th>
                          <th className="py-2 pr-4 font-medium">Request</th>
                          <th className="py-2 pr-4 font-medium">From IP</th>
                          <th className="py-2 pr-4 font-medium">Auth</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diag.map((d) => {
                          const short = (d.path || '').replace('/api/yeastar/crm/', '').replace('/api/yeastar/', '') || d.path;
                          const label = d.event ? d.event : `${d.method} ${short}`;
                          return (
                            <tr key={d.id} className="border-b border-slate-50" data-testid={`pbx-diag-row-${d.id}`}>
                              <td className="py-2 pr-4 whitespace-nowrap text-slate-600">{formatDateTime(d.created_at)}</td>
                              <td className="py-2 pr-4 whitespace-nowrap">
                                <code className="text-xs text-slate-700">{label}</code>
                                {d.action && <span className="ml-2 text-xs text-slate-400">→ {d.action}</span>}
                              </td>
                              <td className="py-2 pr-4 whitespace-nowrap text-slate-500">{d.ip || '—'}</td>
                              <td className="py-2 pr-4 whitespace-nowrap">
                                {d.auth_ok ? (
                                  <span className="inline-flex items-center gap-1 text-green-600"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-rose-600" title={d.reason || ''}><XCircle className="w-3.5 h-3.5" /> {d.reason || 'rejected'}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CallAnalytics;
